import React, { Fragment } from "react";

import { truncate } from "lodash";
import { z } from "zod";

import useGetProposal from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import {
  ConcreteImplementationPlanStatus,
  Rebalancer,
  StandardMaterialityFilter,
  TradeProposalId,
} from "@fartherfinance/frontend/api/Investment/types";
import {
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Card from "@src/sharedComponents/Card/Card";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./ProposalDetails.module.css";

interface Props {
  portfolioPlanAssignmentId: PortfolioPlanAssignmentId | undefined; // undefined means loading
  portfolioPlanId: PortfolioPlanId | undefined;
  proposalId: TradeProposalId | undefined;
}

function isRaisingCash(status: ConcreteImplementationPlanStatus): boolean {
  switch (status) {
    case "Frozen_PendingLiquidationProposalApproval":
      return true;
    case "Frozen_PendingLiquidationTaskCompletion":
      return true;
    case "Liquidating":
      return true;
    case "FullyLiquidating":
      return true;
    case "Active":
      return false;
    case "Inactive":
      return false;
  }
}

const CardPortfolioPlanAssignmentStatus: React.FC<Props> = (props) => {
  const auth = useAdvisorRequestAuth();
  const trade = useGetProposal(props.proposalId ?? null, auth);

  return (
    <Card header="Trading Status">
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Trading</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment === undefined ? (
            <Skeleton width={100} />
          ) : trade.data.portfolioPlanAssignment.intent === "TradingOn" ? (
            "On"
          ) : (
            "Off"
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Raising Cash</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment === undefined ? (
            <Skeleton width={100} />
          ) : isRaisingCash(trade.data.portfolioPlanAssignment.status) ? (
            "Yes"
          ) : (
            "No"
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Status</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment === undefined ? (
            <Skeleton width={100} />
          ) : (
            <Tooltip
              tooltipText={trade.data.portfolioPlanAssignment.status}
              placement="top"
            >
              <Text variant="body">
                {truncate(trade.data.portfolioPlanAssignment.status, {
                  length: 20,
                })}
              </Text>
            </Tooltip>
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Prop ID</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {props.proposalId ?? <Skeleton width={100} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Assignment (PPA) Version</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment.version ?? (
            <Skeleton width={100} />
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">PPA Name</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment.name ?? <Skeleton width={100} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Updated</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment === undefined ? (
            <Skeleton width={100} />
          ) : (
            `${trade.data.portfolioPlanAssignment.updatedAt.toLocaleString(
              "en-US",
              {
                timeZone: "America/New_York",
                dateStyle: "short",
                timeStyle: "short",
              }
            )} (East)`
          )}
        </Text>
      </div>

      <div className={styles.VerticalSeparator} />

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Rebalancer for proposal</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        {trade.data?.rebalancer !== undefined ? (
          <Text variant="body">{rebalancerLabel(trade.data.rebalancer)}</Text>
        ) : (
          <Skeleton width={100} />
        )}
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">PQS ID</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        {trade.data?.pqsUniqueRebalanceId !== undefined ? (
          <Text variant="body">{trade.data.pqsUniqueRebalanceId}</Text>
        ) : (
          <Skeleton width={100} />
        )}
      </div>

      <div className={styles.VerticalSeparator} />

      {(trade.data?.portfolioPlanAssignment.rebalancers ?? [undefined]).map(
        (rebalancer, idx) => (
          <Fragment key={`${rebalancer?.rebalancer ?? ""}-${idx}`}>
            <RebalancerSection
              index={idx + 1}
              rebalancer={rebalancer?.rebalancer}
              materiality={rebalancer?.standardMaterialityFilter}
              isRebalancerUsed={
                rebalancer !== undefined &&
                rebalancer.rebalancer === trade.data?.rebalancer
              }
            />
          </Fragment>
        )
      )}
    </Card>
  );
};

export default CardPortfolioPlanAssignmentStatus;

function rebalancerLabel(rebalancer: Rebalancer): string {
  switch (rebalancer) {
    case "TaxLossHarvestingOnlyRebalancer":
      return "Tax-loss harvesting";
    case "AlphaRebalancer":
      return "Alpha";
    case "LiquidationRebalancer":
      return "Liquidation";
  }
}

type MaterialityFilter = z.infer<typeof StandardMaterialityFilter>;

interface RebalancerSectionProps {
  index: number;
  rebalancer: Rebalancer | undefined;
  materiality: MaterialityFilter | undefined;
  isRebalancerUsed: boolean | undefined;
}

const RebalancerSection: React.FC<RebalancerSectionProps> = (props) => {
  if (props.rebalancer === undefined) {
    return (
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Rebalancer {props.index}</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          <Skeleton width={50} />
        </Text>
      </div>
    );
  }

  return (
    <>
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Rebalancer {props.index}</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">{rebalancerLabel(props.rebalancer)}</Text>
        {props.isRebalancerUsed ? (
          <Text variant="bodyBold">&nbsp;(used)</Text>
        ) : null}
      </div>
      {props.materiality === undefined ? null : (
        <>
          <div className={styles.FlexRow}>
            <Text variant="bodyBold">R{props.index} Arithmetic Thr</Text>
            <Text variant="bodyBold">:&nbsp;</Text>
            <Text variant="body">{props.materiality.arithmeticThreshold}</Text>
          </div>
          <div className={styles.FlexRow}>
            <Text variant="bodyBold">R{props.index} Weight Thr</Text>
            <Text variant="bodyBold">:&nbsp;</Text>
            <Text variant="body">{props.materiality.weightThreshold}</Text>
          </div>
        </>
      )}
    </>
  );
};
